import { Tooltip } from "@altimateai/lego";
import styles from "@/pages/query/styles.module.scss";
import { useState } from "react";
import dayjs from "dayjs";
import StatusAssignerDropdown from "@/pages/explorer/components/statusAssignerDropdown/StatusAssignerDropdown";
import OwnerAssignerDropdown from "@/pages/explorer/components/ownerAssignerDropdown/OwnerAssignerDropdown";
import EffortTag from "@/pages/explorer/components/effortTag/EffortTag";
import { getEffortLabel, getMoneySavings, getOpportunityStatus } from "@/pages/explorer/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { Tab } from "@/components/tab";
import { InfoGray } from "@assets/icons";
import { Resources } from "@/modules/insights/components/Resources";
import Activity from "@/modules/insights/components/Activity";
import { Stack } from "@altimateai/lego";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import React from "react";

interface OpportunityInfoProps {
  opportunity: GetOpportunitiesDataResponse;
  startDate: string | Date;
  endDate: string | Date;
  savingsInfo?: GetOpportunitiesDataResponse["savings_attribution"];
  extraContent?: Record<string, React.ReactNode | string>;
}

const OpportunityInfo = ({
  opportunity,
  startDate,
  endDate,
  savingsInfo,
  extraContent,
}: OpportunityInfoProps) => {
  const { currency, currencySymbol } = useAppState();
  const [assignedStatus, setAssignedStatus] = useState(getOpportunityStatus(opportunity?.status));
  const [assignedOwner, setAssignedOwner] = useState(opportunity?.owner || "-1");
  const startDateStr = dayjs(startDate).toISOString();
  const endDateStr = dayjs(endDate).toISOString();
  return (
    <>
      <div className="al-grid al-grid-cols-1">
        <div className="al-grid al-grid-cols-1 md:al-grid-cols-3 al-gap-4">
          <div>
            <h6>Status</h6>
            <StatusAssignerDropdown
              opportunity_id={opportunity?.opportunity_id}
              assignedStatus={assignedStatus}
              setAssignedStatus={setAssignedStatus}
              startDate={startDateStr}
              endDate={endDateStr}
            />
          </div>
          <div>
            <h6>Effort</h6>
            <EffortTag effort={getEffortLabel(opportunity.effort)} />
          </div>
          <div>
            <h6>First Detected</h6>
            <div>{dayjs(opportunity.first_seen).fromNow()} ago</div>
          </div>
        </div>

        <div className="al-grid al-grid-cols-1 md:al-grid-cols-3 al-gap-4">
          <div>
            <h6>Owner</h6>
            <OwnerAssignerDropdown
              opportunity_id={opportunity?.opportunity_id}
              assignedOwner={assignedOwner}
              setAssignedOwner={setAssignedOwner}
              startDate={startDateStr}
              endDate={endDateStr}
            />
          </div>
          <div>
            <h6>Time Savings</h6>
            <div>{opportunity.time_savings}</div>
          </div>
          <div>
            <h6>Last Detected</h6>
            <div>{dayjs(opportunity.last_seen).fromNow()} old</div>
          </div>
        </div>

        <div className="al-grid al-grid-cols-1 md:al-grid-cols-3 al-gap-4">
          <div></div>
          <div>
            <h6>Money Savings</h6>
            <Stack gap="xs" align="center">
              {getMoneySavings(
                opportunity?.money_savings?.min,
                opportunity?.money_savings?.max,
                currency
              )}
              {String(opportunity?.status) === "2" &&
                savingsInfo?.start_date &&
                savingsInfo?.end_date &&
                savingsInfo?.altimate_estimated_cost_saving && (
                  <Tooltip
                    title={
                      <div>
                        <div>Start Date: {dayjs(savingsInfo?.start_date).format("DD/MM/YYYY")}</div>
                        <div>End Date: {dayjs(savingsInfo?.end_date).format("DD/MM/YYYY")}</div>
                        <div>
                          Estimated Savings: {currencySymbol}
                          {savingsInfo?.altimate_estimated_cost_saving} per day
                        </div>
                      </div>
                    }
                  >
                    <InfoGray />
                  </Tooltip>
                )}
            </Stack>
          </div>
          <div>
            <h6>Count</h6>
            <div>{opportunity.count}</div>
          </div>
        </div>
      </div>
      <div className="divider mt-2 mb-2" />
      <Stack className="al-w-full" align="center">
        <h6>Resources</h6>
        <Resources
          resources={opportunity?.resources || []}
          instance_id={String(opportunity.instance_id)}
          startDate={startDate ? dayjs(startDate).toDate() : undefined}
          endDate={endDate ? dayjs(endDate).toDate() : undefined}
          opportunity_id={opportunity.opportunity_id}
        />
      </Stack>
      {extraContent && (
        <div className="mt-2">
          {Object.entries(extraContent).map(([label, value], index) => (
            <>
              <div className="divider mt-2 mb-2" />
              <Stack key={index} gap="xl" className="mt-2">
                <h6>{label}</h6>
                <div>{value}</div>
              </Stack>
            </>
          ))}
        </div>
      )}
    </>
  );
};

interface OpportunitySummaryProps {
  opportunity: GetOpportunitiesDataResponse;
  startDate: string | Date;
  endDate: string | Date;
  savingsInfo?: GetOpportunitiesDataResponse["savings_attribution"];
  extraContent?: Record<string, React.ReactNode | string>;
  showScheduledEventsActivity?: boolean;
}

const OpportunitySummary = ({
  opportunity,
  startDate,
  endDate,
  savingsInfo,
  extraContent,
  showScheduledEventsActivity,
}: OpportunitySummaryProps) => {
  const tabs = [
    {
      label: "Info",
      component: (
        <OpportunityInfo
          opportunity={opportunity}
          startDate={startDate}
          endDate={endDate}
          savingsInfo={savingsInfo}
          extraContent={extraContent}
        />
      ),
    },
    {
      label: "Activity",
      component: (
        <Activity
          opportunity_id={opportunity?.opportunity_id}
          showScheduledEventsActivity={showScheduledEventsActivity}
        />
      ),
    },
  ];

  return (
    <div
      className={styles.opportunitySummary}
      role="presentation"
      onClick={e => {
        e.stopPropagation();
      }}
      onKeyDown={e => {
        e.stopPropagation();
      }}
    >
      <Tab tabs={tabs} onSelectedTabChange={() => {}} tabStyle={"al-w-full"} pagedTab={false} />
    </div>
  );
};

export { OpportunitySummary };
